/**
 * annotationsGuard.js — заслон хранилища аннотаций. Чистая математика/разбор,
 * ни одного импорта: проверяется в node на строках с заранее известным ответом
 * (tests/annotations_guard_test.mjs).
 *
 * ЗАЧЕМ ЭТОТ МОДУЛЬ, целиком.
 *
 * В базе владельца лежала запись серии на 245 767 аннотаций (12 настоящих +
 * 245 755 точных копий — следствие удвоения за каждое открытие серии, само
 * удвоение остановлено в 11.1.89). Разбор показал ТРИ независимых дыры, и все
 * три закрываются здесь, в одном месте:
 *
 * 1. ПАМЯТЬ. Раздутая запись — строка на сотни мегабайт. `JSON.parse` целиком
 *    строит В ПАМЯТИ четверть миллиона объектов разом (всплеск больше
 *    гигабайта) — и только потом их выбрасывали. Владелец прямо сказал: «это
 *    отнимает память». Теперь строка разбирается ПОЭЛЕМЕНТНО: сканер идёт по
 *    символам, вырезает по одному элементу верхнего массива и отдаёт его
 *    обработчику. В памяти одновременно — исходная строка и ТОЛЬКО годные
 *    аннотации (их не больше потолка), а не весь мусор.
 *
 * 2. СЛУЖЕБНЫЕ АННОТАЦИИ. В хранилище попадали аннотации перекрестий
 *    (Crosshairs, ~24 КБ каждая — журнал 11.1.75: «Crosshairs — 23781
 *    знаков»). У них при КАЖДОМ включении инструмента новые UID, поэтому
 *    защита от повторов их не ловит: записи росли и после 11.1.89. Хранить
 *    их бессмысленно вдвойне: при следующем сеансе инструмент создаёт свои,
 *    а восстановленные висят мёртвым грузом. Фильтр по имени инструмента
 *    отделяет рукотворные измерения от служебной обвязки.
 *
 * 3. ЧУЖИЕ СЕРИИ. Память аннотаций при смене серии не очищается (и не должна:
 *    в сравнении открыты две серии разом), а автосохранение выгружало ВСЁ
 *    подряд под ключ текущей серии. Так измерения серии А попадали в запись
 *    серии Б, записи взаимно заражались и росли. Принадлежность аннотации
 *    серии решается по её же метаданным: referencedImageId → кадры серии,
 *    volumeId → том серии, FrameOfReferenceUID → система координат.
 */

/**
 * Обойти элементы ВЕРХНЕГО массива JSON-строки, не разбирая строку целиком.
 *
 * Сканер честный, а не «до первой запятой»: строки с запятыми и скобками
 * внутри (`"текст, [так]"`), экранированные кавычки (`\"`), вложенные объекты
 * и массивы любой глубины проходятся по правилам JSON. Чего сканер НЕ делает —
 * не проверяет синтаксис элемента: это работа JSON.parse над каждым элементом
 * по отдельности (у обработчика).
 *
 * @param {string} строка — предположительно JSON-массив
 * @param {(текст: string, номер: number) => (boolean|void)} [обработчик]
 *        — получает текст очередного элемента; вернёт false — обход прерван.
 *        Без обработчика функция просто считает элементы (это дёшево: одна
 *        прогонка по символам, без единого JSON.parse).
 * @returns {{всего: number, массив: boolean, остановлено: boolean}}
 */
export function перечислитьЭлементыМассиваJSON(строка, обработчик = null) {
  if (typeof строка !== 'string') return { всего: 0, массив: false, остановлено: false };
  const n = строка.length;
  let i = 0;
  while (i < n && строка.charCodeAt(i) <= 32) i++;
  if (строка[i] !== '[') return { всего: 0, массив: false, остановлено: false };
  i++;
  let всего = 0;
  let остановлено = false;
  while (i < n) {
    while (i < n && (строка.charCodeAt(i) <= 32 || строка[i] === ',')) i++;
    if (i >= n) break;
    if (строка[i] === ']') break;
    const старт = i;
    let глубина = 0;
    let вСтроке = false;
    for (; i < n; i++) {
      const ch = строка[i];
      if (вСтроке) {
        if (ch === '\\') { i++; continue; }         // экранированный символ — пропустить следующий
        if (ch === '"') вСтроке = false;
        continue;
      }
      if (ch === '"') { вСтроке = true; continue; }
      if (ch === '{' || ch === '[') { глубина++; continue; }
      if (ch === '}') { глубина--; continue; }
      if (ch === ']') {
        if (глубина === 0) break;                    // закрылся ВЕРХНИЙ массив
        глубина--; continue;
      }
      if (ch === ',' && глубина === 0) break;        // конец элемента
    }
    const текст = строка.slice(старт, i);
    if (текст.trim()) {
      всего++;
      if (обработчик && обработчик(текст, всего - 1) === false) { остановлено = true; break; }
    }
  }
  return { всего, массив: true, остановлено };
}

/**
 * Просеять запись аннотаций: повторы по UID, служебные инструменты, брак.
 *
 * Принимает и строку (тогда работает поэлементный сканер — см. пункт 1 в
 * шапке), и уже разобранный массив. Возвращает ГОДНЫЕ объекты (не больше
 * потолка) и полный счёт — по нему решается, раздута ли запись.
 *
 * @param {string|Array} вход
 * @param {object} [п]
 * @param {number}  [п.потолок=5000] — больше стольких измерений на серию
 *        человек не делает; всё сверх — размножение, в память не берётся
 * @param {(имяИнструмента: string) => boolean} [п.хранитсяЛи] — фильтр
 *        служебных; не задан — хранится всё
 * @param {Set<string>} [п.ужеЕсть] — UID, уже лежащие в памяти; множество
 *        ПОПОЛНЯЕТСЯ принятыми, чтобы два вызова подряд не внесли одно дважды
 * @returns {{годные: Array, всего: number, повторов: number,
 *            повторовВЗаписи: number, повторовСПамятью: number,
 *            битых: number, служебных: number, массив: boolean}}
 *
 * ДВА ВИДА ПОВТОРОВ РАЗЛИЧАЮТСЯ, и это не педантизм. Повтор ВНУТРИ ЗАПИСИ —
 * это мусор размножения: запись надо переписать. Повтор С ПАМЯТЬЮ — это
 * серия, открытая второй раз за сеанс: запись ЧИСТА, переписывать её (и
 * пугать врача сообщением о чистке) не за что. Прежний общий счётчик их не
 * различал, и по нему нельзя было решить, раздута запись или нет.
 */
export function просеятьАннотации(вход, { потолок = 5000, хранитсяЛи = null, ужеЕсть = null } = {}) {
  const виденные = ужеЕсть instanceof Set ? ужеЕсть : new Set();
  const изЭтойЗаписи = new Set();
  const годные = [];
  let повторовВЗаписи = 0, повторовСПамятью = 0, битых = 0, служебных = 0, всего = 0, переполнение = 0;

  const принять = (ann) => {
    if (!ann || typeof ann !== 'object' || !ann.annotationUID || !ann.metadata) { битых++; return; }
    if (typeof хранитсяЛи === 'function' && !хранитсяЛи(ann.metadata.toolName)) { служебных++; return; }
    if (изЭтойЗаписи.has(ann.annotationUID)) { повторовВЗаписи++; return; }
    изЭтойЗаписи.add(ann.annotationUID);
    if (виденные.has(ann.annotationUID)) { повторовСПамятью++; return; }
    if (годные.length >= потолок) { переполнение++; return; }
    виденные.add(ann.annotationUID);
    годные.push(ann);
  };

  if (typeof вход === 'string') {
    const итог = перечислитьЭлементыМассиваJSON(вход, (текст) => {
      всего++;
      let ann = null;
      try { ann = JSON.parse(текст); } catch (e) { битых++; return; }
      принять(ann);
    });
    if (!итог.массив) return _пустоеСито();
  } else if (Array.isArray(вход)) {
    for (const ann of вход) { всего++; принять(ann); }
  } else {
    return _пустоеСито();
  }
  return {
    годные, всего, битых, служебных, переполнение, массив: true,
    повторовВЗаписи, повторовСПамятью,
    повторов: повторовВЗаписи + повторовСПамятью,
  };
}

function _пустоеСито() {
  return { годные: [], всего: 0, повторов: 0, повторовВЗаписи: 0, повторовСПамятью: 0, битых: 0, служебных: 0, переполнение: 0, массив: false };
}

/**
 * Принадлежит ли аннотация серии. Решается по СОБСТВЕННЫМ метаданным
 * аннотации, в порядке убывания точности; сверено с cornerstone 5.6.3:
 *
 *   1. referencedImageId — есть у нарисованных и в стек-окнах, и в объёмных
 *      (annotationHydration/getReferencedImageId выводит его из кадров тома,
 *      когда плоскость взгляда совпадает с плоскостью съёмки);
 *   2. volumeId — окна-реформаты кладут его в metadata через getViewReference
 *      (BaseVolumeViewport.getViewReference: `target.volumeId = volumeId`);
 *   3. FrameOfReferenceUID — общая система координат исследования.
 *
 * Правило при НЕЗНАНИИ — оставить: аннотацию, которую не удалось опознать,
 * считаем своей. Цена ошибки несимметрична: лишняя строка в записи — копейки,
 * выброшенное измерение врача — потеря работы.
 *
 * @param {object} ann — аннотация cornerstone
 * @param {object} [владение]
 * @param {Set<string>|null} [владение.кадры] — imageId кадров серии
 * @param {string|null} [владение.том] — подстрока идентификатора тома серии
 *        (например `volume_<seriesUID>`)
 * @param {string|null} [владение.frameOfReference]
 * @returns {boolean}
 */
export function аннотацияПринадлежитСерии(ann, владение = {}) {
  const м = ann?.metadata || {};
  const кадры = владение.кадры instanceof Set ? владение.кадры : null;
  if (м.referencedImageId) {
    if (кадры) return кадры.has(м.referencedImageId);
    return true;
  }
  const том = владение.том ? String(владение.том) : null;
  if (м.volumeId) {
    if (том) return String(м.volumeId).includes(том);
    return true;
  }
  const fr = владение.frameOfReference || null;
  if (м.FrameOfReferenceUID && fr) return м.FrameOfReferenceUID === fr;
  return true;
}
